import { iconSizes } from '@mds/tokens';
import { paginationItemClasses, type Components } from '@mui/material';
import { CSSProperties } from 'react';
import { themeVars } from '../themeVars';
import type { Theme } from './utils';

export const overlay: CSSProperties = {
  content: '""',
  position: 'absolute',
  width: '50%',
  height: '100%',
  pointerEvents: 'none',
  left: '25%',
  bottom: 0,
};

export const iconHover = (theme: Theme) => ({
  content: '""',
  position: 'absolute',
  width: '70%',
  height: '70%',
  pointerEvents: 'none',
  left: '15%',
  top: '15%',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: themeVars(theme).palette.action.hover,
});

export const MuiPaginationItem: Components<Theme>['MuiPaginationItem'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      minWidth: 40,
      height: 40,
      borderRadius: theme.shape.borderRadius,

      '&:hover': {
        backgroundColor: 'transparent',
      },

      [`&.${paginationItemClasses.focusVisible}`]: {
        backgroundColor: 'transparent',
        boxShadow: `inset 0 0 0 1px ${themeVars(theme).palette.border.focused}, inset 0 0 0 2px ${themeVars(theme).palette.background.default}`,
      },

      [`&.${paginationItemClasses.selected}`]: {
        color: themeVars(theme).palette.text.primary,
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: 'transparent',
        },
        [`&.${paginationItemClasses.focusVisible}`]: {
          backgroundColor: 'transparent',
          boxShadow: `inset 0 0 0 1px ${themeVars(theme).palette.border.focused}, inset 0 0 0 2px ${themeVars(theme).palette.background.default}`,
        },
      },
    }),

    previousNext: ({ theme }) => ({
      '@media (hover: hover)': {
        '&:hover:before': {
          ...iconHover(theme),
        },
      },
      '&:active:before': {
        ...iconHover(theme),
        backgroundColor: themeVars(theme).palette.action.pressed,
      },
      '& svg': {
        fontSize: iconSizes.xs,
      },
    }),

    firstLast: ({ theme }) => ({
      '@media (hover: hover)': {
        '&:hover:before': {
          ...iconHover(theme),
        },
      },
      '&:active:before': {
        ...iconHover(theme),
        backgroundColor: themeVars(theme).palette.action.pressed,
      },
    }),

    page: ({ theme }) => ({
      '&:before': {
        ...overlay,
        borderBlockEnd: `4px solid transparent`,
        transition: theme.transitions.create(['border-width', 'border-color'], {
          duration: theme.transitions.duration.short,
        }),
      },

      '&:hover:before': {
        borderColor: themeVars(theme).palette.border.subtle,
      },

      [`&.${paginationItemClasses.selected}`]: {
        '&:hover:before': {
          borderColor: themeVars(theme).palette.border.strong,
        },
        [`&:before`]: {
          borderColor: themeVars(theme).palette.border.focused,
        },
        [`&.${paginationItemClasses.focusVisible}`]: {
          backgroundColor: 'transparent',
          boxShadow: `inset 0 0 0 1px ${themeVars(theme).palette.border.focused}, inset 0 0 0 2px ${themeVars(theme).palette.background.default}`,
        },
        [`&.${paginationItemClasses.disabled}`]: {
          backgroundColor: 'transparent',
          '&:before': {
            borderColor: themeVars(theme).palette.text.disabled,
            opacity: 0.7,
          },
        },
      },
    }),

    ellipsis: ({ theme }) => ({
      height: 'auto',
      borderRadius: theme.shape.borderRadius,
    }),
  },

  variants: [
    {
      props: { size: 'small' },
      style: ({ theme }) => ({
        minWidth: 32,
        height: 32,
        [`&.${paginationItemClasses.page}`]: {
          borderRadius: theme.shape.borderRadius,
        },
        [`&.${paginationItemClasses.ellipsis}`]: {
          height: 'auto',
          borderRadius: theme.shape.borderRadius,
        },
      }),
    },
    {
      props: { size: 'large' },
      style: ({ theme }) => ({
        minWidth: 40,
        height: 40,
        [`&.${paginationItemClasses.page}`]: {
          borderRadius: theme.shape.borderRadius,
        },
        [`&.${paginationItemClasses.ellipsis}`]: {
          height: 'auto',
          borderRadius: theme.shape.borderRadius,
        },
      }),
    },
  ],
};
